import React, { forwardRef, useImperativeHandle } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { TooltipPositions } from '../../../components/tooltips/tooltipConstants';
import FloatingPanel from '../../../components/tooltips/FloatingPanel';
import PopupPanelMouseManager from '../../../components/popupPanel/PopupPanelMouseManager';
import Button from '../../../components/buttons/Button';
import Icon from '../../../components/icons/Icon';
import FormulaDropdownListItem from './FormulaDropdownListItem';
import { PopupIds } from '../../../components/popupPanel/popupConstants';
import { useTranslator } from '../../../components/i18n/useTranslator';
import useFormulaDropdownKeyboardControls from './useFormulaDropdownKeyboardControls';
import useFormulaPickerController from './useFormulaPickerController';
import type { FormulaPickerRef } from './useFormulaPickerController';

import './FormulaDropdown.scss';

export type FormulaDropdownProps = {
    show: boolean;
    setShow: Dispatch<SetStateAction<boolean>>;
    addFormulaToCell: (formula: string, replacePrevCharacters: number) => void;
};

const FormulaDropdown = forwardRef<FormulaPickerRef, FormulaDropdownProps>(function FormulaDropdownContent(
    { addFormulaToCell, show, setShow },
    ref,
) {
    const {
        focusedIndex,
        formulaPickerData,
        formulaQuery,
        hideFormulaPicker,
        listFocusManager,
        selectFormula,
        setFormulaPickerQuery,
        showFormulaPicker,
    } = useFormulaPickerController({ show, setShow, addFormulaToCell, replaceFormulaQueryOnInsert: true });

    const translate = useTranslator('popup', 'table.formula');

    useFormulaDropdownKeyboardControls({
        enabled: showFormulaPicker,
        focusedFormula: formulaPickerData[focusedIndex],
        listFocusManager,
        selectFormula,
    });

    useImperativeHandle(ref, () => ({ setFormulaPickerQuery, hideFormulaPicker }), [
        hideFormulaPicker,
        setFormulaPickerQuery,
    ]);

    const openFormulaDocumentation = () => {
        const newWindow = window.open(
            'https://help.milanote.com/en/articles/8584809-formulas-functions-for-tables',
            '_blank',
            'noopener,noreferrer',
        );

        if (newWindow) {
            newWindow.opener = null;
        }
    };

    if (!showFormulaPicker) return null;

    return (
        <FloatingPanel
            arrowColor="var(--ui-modal-background-primary)"
            arrowStrokeColor="var(--ui-modal-background-primary-hover)"
            className="FormulaDropdown"
            selector=".Table.selected .handsontable-input-holder"
            position={TooltipPositions.BOTTOM}
            pollPosition
        >
            <PopupPanelMouseManager
                addFormulaToCell={addFormulaToCell}
                showFormulaDropdown={show}
                setShowFormulaDropdown={setShow}
                visible={showFormulaPicker}
                popupId={PopupIds.FORMULA_DROPDOWN}
                closePopup={hideFormulaPicker}
            >
                <ul className="formula-list" data-table-util={true}>
                    {formulaPickerData.map((formula, index) => (
                        <FormulaDropdownListItem
                            key={formula}
                            formulaQuery={formulaQuery}
                            focusedIndex={focusedIndex}
                            formula={formula}
                            index={index}
                            onSelectFormula={selectFormula}
                        />
                    ))}
                </ul>
                <div className="formula-docs-link">
                    <Button onMouseDown={openFormulaDocumentation}>
                        <span>{translate('helpLink')}</span>
                        <Icon name="chevron-right" />
                    </Button>
                </div>
            </PopupPanelMouseManager>
        </FloatingPanel>
    );
});

export default FormulaDropdown;
